export const personalInfo = {
  name: "Samarpan Mohanty",
  filename: "samarpan_mohanty.ipynb",
  kernel: "materials-ml",
  python: "Python 3",
  headline: "I build machine-learning systems that experimental scientists can actually run.",
  location: "Lincoln, Nebraska, USA",
  email: "samarpanmohanty3347@gmail.com",
  phone: "+1 (725) 322 3944",
  linkedin: "https://linkedin.com/in/samarpan-mohanty-8b3bb0278",
  github: "https://github.com/Samarpan1122",
  scholar: "https://scholar.google.com/citations?user=0XBzK3wAAAAJ",
  orcid: "https://orcid.org/0009-0001-1309-7425",
  website: "https://samarpan-portfolio.netlify.app/",
  cv: "/Samarpan_Mohanty_CV.pdf",
};

export const now = {
  roles: [
    {
      title: "Founder & CEO",
      org: "Invar AI",
      href: "https://invar.it.com",
      when: "2024–Present",
      note: "Research-driven applied AI with a faculty advisor and a graduate researcher. NSF I-Corps.",
    },
    {
      title: "Research Assistant — ML for materials",
      org: "PSE Bavarian Lab, UNL",
      when: "Sep 2024–Present",
      note: "Polymer informatics in TensorFlow / PyTorch / TPOT; GNNs on polymer and crystal structures; compound screening for experimental validation.",
    },
    {
      title: "Intern",
      org: "NCESR, UNL",
      when: "Summer 2026",
      note: "Material digital twins and applied energy-sciences research. Symposium poster and report.",
    },
  ],
  latest: {
    paper: {
      title: "LiClustCondAI",
      venue: "Journal of Power Sources",
      role: "Co-first author",
      href: "https://doi.org/10.1016/j.jpowsour.2026.240078",
    },
    product: {
      title: "CopolPred",
      href: "https://copolpred.com",
    },
  },
};

export const education = {
  degree: "B.S. Computer Engineering",
  university: "University of Nebraska–Lincoln",
  expected: "2028",
  honors: "Dean’s List, Spring 2025",
};

export const experience = [
  {
    org: "Invar AI",
    href: "https://invar.it.com",
    role: "Founder & CEO",
    where: "Lincoln, NE",
    when: "2024–Present",
    bullets: [
      "Co-founded a research-driven technology startup with a faculty advisor and a graduate researcher, focused on applied AI systems and scalable data-driven platforms.",
      "Led product vision, technical architecture, and research direction; built early prototypes and coordinated cross-functional development.",
      "Selected for the NSF I-Corps Program: customer discovery, market analysis, and stakeholder interviews.",
    ],
  },
  {
    org: "Nebraska Center for Energy Sciences Research (NCESR)",
    role: "Intern",
    where: "Lincoln, NE",
    when: "Summer 2026",
    bullets: [
      "Selected for NCESR’s competitive summer research internship in interdisciplinary energy sciences.",
      "Computational modeling of material digital twins, data analysis, and applied research in sustainable energy systems.",
      "Presented at the UNL Summer Research Symposium; prepared a formal research poster and report.",
    ],
  },
  {
    org: "PSE Bavarian Lab, University of Nebraska–Lincoln",
    role: "Research Assistant — Machine Learning for Materials Science",
    where: "Lincoln, NE",
    when: "Sep 2024–Present",
    bullets: [
      "Polymer informatics models in TensorFlow / PyTorch and TPOT for property prediction (ionic conductivity, permeability, fouling metrics) to guide polymer and solid-state electrolyte design.",
      "Graph neural networks on polymer and crystal structures; end-to-end compound screening that ingests composition / formulation data and ranks materials for experimental validation.",
    ],
  },
  {
    org: "Razavi Lab, University of Nebraska–Lincoln",
    role: "Research Assistant — Biomedical Data Analysis",
    where: "Lincoln, NE",
    when: "Nov 2024–Dec 2024",
    bullets: [
      "Trained models that raised lymphatic-flow prediction accuracy by 20%; prepared and validated biological datasets with Python / Pandas.",
    ],
  },
];

export const fellowships = [
  {
    when: "2026–2027",
    title: "UCARE Research Fellowship",
    org: "University of Nebraska–Lincoln",
    detail:
      "Deep learning for polymer copolymerization reactivity-ratio prediction.",
  },
  {
    when: "2025–2026",
    title: "UCARE Research Fellowship",
    org: "University of Nebraska–Lincoln",
    detail:
      "Machine learning to accelerate solid-state battery development.",
  },
];

export const projects = [
  {
    id: "copolpred",
    featured: true,
    title: "CopolPred",
    kind: "platform",
    href: "https://copolpred.com",
    figure: "/figures/copolpred.jpg",
    figureCaption: "CopolPred workspace — r₁, r₂ from monomer SMILES. Click to open the live tool.",
    blurb:
      "Machine-learning platform for predicting copolymerization reactivity ratios from monomer structures and experimental data.",
    points: [
      "Automated preprocessing, molecular feature generation, training, hyperparameter search, cross-validation, and inference.",
      "Sequence- and structure-aware deep-learning and ensemble architectures.",
      "Interactive evaluation: predicted vs actual, loss curves, residuals, model comparison, feature importance.",
    ],
  },
  {
    id: "molytics",
    featured: true,
    title: "Molytics",
    kind: "library",
    figure: "/figures/invar.jpg",
    figureCaption: "Molytics, shipped through Invar — when a materials prediction is supported.",
    blurb:
      "Automated ML library for compound property prediction. 85%+ accuracy; up to 15% over prior models. Feature selection and tuning cut manual setup about 90%.",
    points: [
      "Interactive web GUI for one-click screening and visualization — no local install required.",
      "Companion paper (in preparation) on clustering-based ensemble regression for unseen materials.",
    ],
  },
  {
    id: "ring",
    featured: true,
    title: "Ring Companion",
    kind: "native iOS",
    figure: "/figures/ring.jpg",
    figureCaption: "OEM ring, charging coil, and the USB BLE dongle used to capture GATT traffic.",
    blurb:
      "Native Swift 5 / SwiftUI app for a low-cost OEM smart ring. Reverse-engineered a proprietary BLE GATT protocol with no vendor docs.",
    points: [
      "Mapped services and characteristics, decoded binary packets, probed command opcodes.",
      "CoreBluetooth implementation plus a Python / Bleak packet-capture CLI for reproducible protocol tests.",
      "HRV (RMSSD), resting HR, Karvonen zones, TRIMP load, sleep debt, recovery, stress, focus, anomaly detection.",
      "Confidence-graded packet interpretations: confirmed, partial, unconfirmed, unknown.",
    ],
  },
  {
    id: "protectsus",
    featured: true,
    title: "ProtectSUS",
    kind: "product",
    href: "https://protectsus.tech",
    repo: "https://github.com/samarpan1122/protectSUS",
    figure: "/figures/protectsus.jpg",
    figureCaption: "ProtectSUS — multi-agent security copilot. Click to open the product.",
    blurb:
      "Multi-agent AI security copilot. Red-team / blue-team LLM agents debate severity and exploitability; a synthesizer writes the fix. Winner, MongoDB Track, NexHacks 2025.",
    points: [
      "Scans GitHub repos, opens patches as pull requests, iterates on /approve and /deny.",
      "ML fix-approval predictor (20+ features) estimates human acceptance before suggesting a change.",
      "MongoDB RAG over historical vulnerabilities and successful fixes.",
      "Next.js 14, TypeScript, Python, PyTorch, Docker, AWS EC2, GitHub OAuth.",
    ],
  },
  {
    id: "memora",
    featured: true,
    title: "Memora",
    kind: "native iOS",
    repo: "https://github.com/sm1216/Memora",
    blurb:
      "SwiftUI albums keyed to writable NFC stickers — photos, stories, maps, one tap.",
    points: [
      "NFC / NDEF scan and write, deep-link routing, location-based browsing.",
      "Mapbox trip maps and a 3D globe. Supabase for persistence.",
    ],
  },
  {
    id: "omnigenome",
    featured: false,
    title: "OmniGenome",
    kind: "research",
    repo: "https://github.com/collaborativebioinformatics/omnigenome",
    blurb:
      "Federated pangenome graphs so biobanks can collaborate without sharing raw genomes. CMU × NVIDIA hackathon, 2026.",
    points: [
      "PGGB and vg on HPRC long-read assemblies (chr19, chr22).",
      "APOE-locus background hashing for privacy-preserving Alzheimer’s association analysis.",
    ],
  },
  {
    id: "canpoldb",
    featured: false,
    title: "CanPolDB",
    kind: "database",
    blurb:
      "Polymeric anticancer-drug literature database across US, Europe, and Japan institutions, with a ProDrugDB-style browser.",
    points: [
      "Automated literature mining: extract, verify, deduplicate, structure.",
      "Formulation, monomers, APIs, architecture, MW, release / PK, efficacy, survival, sources.",
    ],
  },
  {
    id: "prodrugdb",
    featured: false,
    title: "ProDrugDB",
    kind: "database",
    href: "https://prodrugdb.com",
    figure: "/figures/prodrugdb.png",
    figureCaption: "ProDrugDB sign-in — the public database at prodrugdb.com.",
    blurb:
      "Searchable prodrug research database: chemical, biological, pharmacological, and literature metadata.",
    points: [
      "Search, browsing, statistics, body-based exploration, pathway-oriented analysis.",
    ],
  },
  {
    id: "kg",
    featured: false,
    title: "KG Model Garbage Collection",
    kind: "research",
    repo: "https://github.com/Samarpan1122/Model_Garbage_Collection",
    blurb:
      "Detect and prune incorrect assertions in biomedical knowledge graphs. +42% factual precision, +35% interpretability / audit confidence (hackathon figures). Continuing with Ben Busby (NVIDIA).",
    points: [],
  },
  {
    id: "banana",
    featured: false,
    title: "Banana4U",
    kind: "desktop",
    href: "https://banana4-u.vercel.app",
    repo: "https://github.com/pujara-narayana/Banana4U",
    figure: "/figures/banana4u.jpg",
    figureCaption: "Banana4U — CornHacks 2025, Banana Track.",
    blurb:
      "Cross-platform Electron companion (Gemini + ElevenLabs). 1st place, Banana Track, CornHacks 2025.",
    points: [],
  },
  {
    id: "vr",
    featured: false,
    title: "AR/VR web browser",
    kind: "mobile VR",
    blurb:
      "Live web pages inside mobile VR on Android. Gaze / head-tracked selection, 95% hit accuracy with dwell-time.",
    points: [],
  },
];

export const projectById = Object.fromEntries(projects.map((p) => [p.id, p]));

export const wall = [
  {
    id: "copolpred",
    span: "feature",
    media: "photo",
    cover: "/figures/copolpred.jpg",
    badge: "Product",
    tags: ["Python", "ML"],
  },
  {
    id: "protectsus",
    span: "wide",
    media: "photo",
    cover: "/figures/protectsus.jpg",
    badge: "Product",
    tags: ["Next.js", "Agents"],
  },
  {
    id: "molytics",
    span: "tall",
    media: "diagram",
    diagram: "clusters",
    badge: "Library",
    tags: ["PyTorch", "AutoML"],
  },
  {
    id: "invar",
    span: "mid",
    media: "photo",
    cover: "/figures/invar.jpg",
    badge: "Company",
    tags: ["NSF I-Corps"],
    title: "Invar AI",
    blurb: "Research-driven applied AI. The company, not a side quest.",
    href: "https://invar.it.com",
    external: true,
  },
  {
    id: "ring",
    span: "mid",
    media: "diagram",
    diagram: "ble",
    badge: "Device",
    tags: ["Swift", "BLE"],
  },
  {
    id: "banana",
    span: "mid",
    media: "photo",
    cover: "/figures/banana4u.jpg",
    badge: "Hackathon",
    tags: ["Electron"],
  },
  {
    id: "paper-liclust",
    span: "text",
    media: "paper",
    badge: "Paper",
    title: "LiClustCondAI",
    blurb:
      "Clustering-based ensemble regression for ionic conductivity of unseen solid-state electrolytes. Journal of Power Sources. Co-first author.",
    href: "https://doi.org/10.1016/j.jpowsour.2026.240078",
    external: true,
    tags: ["Materials"],
  },
];

export const publications = [
  {
    id: "5psfj",
    file: "5psfj.md",
    authors: "Mohanty, S., et al.",
    title:
      "Towards Federated Learning Across Biobanks: Prototype Software from the 2026 Carnegie Mellon University–NVIDIA Hackathon",
    venue: "BioHackrXiv (Center for Open Science)",
    year: "2026",
    href: "https://osf.io/preprints/biohackrxiv/5psfj",
    note: "CMU × NVIDIA Federated Learning Hackathon; AWS Open Data and NVIDIA Omics. Related lab work: OmniGenome.",
  },
  {
    id: "g4rk2",
    file: "g4rk2.md",
    authors: "Peyton, M. P., Chung, C.-H., Mohanty, S., et al.",
    title:
      "A Blueprint for Open Science: How Transatlantic Teams Built and Deployed Knowledge Graphs to Enable Biological (AI) Models",
    venue: "BioHackrXiv (Center for Open Science)",
    year: "2025",
    href: "https://osf.io/preprints/biohackrxiv/g4rk2",
    note: "Transatlantic open-science team with NVIDIA and AWS.",
  },
  {
    id: "liclust",
    file: "liclustcondai.md",
    authors: "Paul, B.; Mohanty, S.; Guo, H.; Bavarian, M.",
    title:
      "LiClustCondAI: A Clustering-Based Ensemble Regression Framework for Generalizing Ionic Conductivity Predictions to Unseen Solid-State Electrolytes",
    venue: "Journal of Power Sources",
    year: "2026",
    href: "https://doi.org/10.1016/j.jpowsour.2026.240078",
    note: "Co-first authors (Paul, Mohanty, Guo).",
  },
  {
    id: "molytics-paper",
    file: "molytics.md",
    authors: "Mohanty, S.; Paul, B.; Guo, H.; Bavarian, M.",
    title:
      "Molytics: A Clustering-Based Ensemble Regression Framework for Generalizing Property Predictions to Unseen Materials",
    venue: "manuscript in preparation",
    year: "2026",
    href: null,
    note: "Co-first authors.",
  },
  {
    id: "sequence-paper",
    file: "sequence-aware-polymers.md",
    authors: "Mohanty, S.; et al.",
    title:
      "Sequence-Aware Deep Learning for Polymer or Copolymer Property Prediction",
    venue: "manuscript in preparation",
    year: "2026",
    href: null,
    note: null,
  },
];

export const talks = [
  {
    kind: "talk",
    year: "2026",
    title:
      "Sequence-Programmed Active Drug Engineering (SPADE Twin): An AI Digital Twin for Reaction-Centric Drug Design",
    where: "AIChE Annual Meeting, Minneapolis, MN",
  },
  {
    kind: "talk",
    year: "2026",
    title: "Multiscale AI Framework for Precision Medicine",
    where: "AIChE Annual Meeting, Minneapolis, MN",
  },
  {
    kind: "talk",
    year: "2026",
    title:
      "Hybrid Deep Learning Framework for Prediction of Copolymer Reactivity Ratios",
    where: "AIChE Annual Meeting, Minneapolis, MN",
  },
  {
    kind: "talk",
    year: "2026",
    title:
      "AI-Driven Platform for Prediction and Inverse Design in Chemistry and Materials Science",
    where: "AIChE Annual Meeting, Minneapolis, MN",
  },
  {
    kind: "talk",
    year: "2026",
    title:
      "Clustering-Guided Framework for Reliable Prediction of Ionic Conductivity in Solid-State Electrolytes for Lithium-Ion Batteries",
    where: "AIChE Annual Meeting, Minneapolis, MN",
  },
  {
    kind: "poster",
    year: "2026",
    title:
      "Understanding Sequential Relevance for Polymer Development Using Deep Learning",
    where: "AIChE Annual Meeting, Minneapolis, MN",
  },
  {
    kind: "talk",
    year: "2026",
    title:
      "Molytics: Support-Aware Machine Learning for Materials-Property Prediction",
    where: "Lightning talk, Husker AI Days Student Spotlight, UNL",
  },
  {
    kind: "poster",
    year: "2026",
    title: "Energy-Sciences and Computational Materials Research",
    where: "NCESR Summer Internship Research Symposium, UNL, Aug 5, 2026",
  },
  {
    kind: "poster",
    year: "2026",
    title:
      "Accelerating Solid-State Battery Electrolyte Design via Automated Machine Learning",
    where: "UNL Student Research Days, Apr 7–8, 2026",
  },
  {
    kind: "poster",
    year: "2025",
    title:
      "Accelerating Solid-State Battery Electrolyte Design via Automated Machine Learning",
    where: "UNL Summer Research Symposium, Aug 5, 2025",
  },
];

export const ventures = [
  {
    year: "2026",
    title: "Invar: An Applied AI and Scientific-Technology Venture",
    where: "New Venture Competition, Nebraska Center for Entrepreneurship, Apr 10, 2026",
  },
  {
    year: "2026",
    title: "Invar: Engineering Innovation and Commercialization Pitch",
    where: "Engineering Pitch Competition, College of Engineering and NUtech Ventures, Mar 24, 2026",
  },
  {
    year: "2026",
    title:
      "Invar: Research-Driven Artificial Intelligence for Scientific and Data-Intensive Applications",
    where: "Hult Prize Qualifiers, Feb 27, 2026",
  },
  {
    year: "2025–2026",
    title:
      "Invar: Customer Discovery, Value Proposition, and Commercialization Strategy",
    where: "NSF I-Corps / Great Plains I-Corps Program",
  },
];

export const awards = [
  {
    year: "2026",
    title: "2nd Place — Best Lightning Talk",
    org: "Husker AI Days Student Spotlight, PRAIRIE Initiative & College of Engineering, UNL",
    detail: "Molytics.",
  },
  {
    year: "2026",
    title: "Winner — MongoDB Track",
    org: "NexHacks",
    detail: "ProtectSUS.",
  },
  {
    year: "2026",
    title: "UCARE Summer Research Award",
    org: "University of Nebraska–Lincoln",
  },
  {
    year: "2026",
    title: "Darrell J. Nelson NCESR Summer Internship",
    org: "Nebraska Center for Energy Sciences Research",
  },
  {
    year: "2026",
    title: "Selected participant — Biomedical Applications Track",
    org: "CMU × NVIDIA Federated Learning Hackathon (AWS Open Data)",
  },
  {
    year: "2025",
    title: "Dean’s List",
    org: "University of Nebraska–Lincoln, Spring 2025",
  },
  {
    year: "2025",
    title: "1st Place — Banana Track",
    org: "CornHacks",
    detail: "Banana4U.",
  },
  {
    year: "2024",
    title: "San Francisco Tech Summit (fully funded)",
    org: "Selected from 5000+ applicants",
  },
  {
    year: "2023",
    title: "Best PCB Design Award",
    org: "$100 — high-efficiency printed circuit board layout",
  },
  {
    year: "2021",
    title: "4th Place",
    org: "Syntax v7.1 Hackathon, 100+ teams",
  },
];

export const skills = {
  programming: ["Python", "Swift", "SwiftUI", "JavaScript", "C", "C#", "Java", "SQL"],
  ml: [
    "Deep learning",
    "AutoML",
    "Ensemble methods",
    "Graph neural networks",
    "Sequence models",
    "NLP",
    "Hyperparameter search",
  ],
  science: [
    "Materials informatics",
    "Computational chemistry",
    "Molecular representation",
    "Biomedical data",
    "Signal processing",
  ],
  mobile: ["SwiftUI", "CoreBluetooth", "NFC/NDEF", "BLE reverse engineering"],
  web: ["React", "Next.js", "Node.js", "Flask", "REST"],
  infra: ["Git", "Docker", "Xcode", "AWS", "Redis", "Celery", "Supabase", "Firebase"],
};

export const toc = [
  { id: "intro", label: "title", n: null },
  { id: "now", label: "current()", n: 1 },
  { id: "experience", label: "appointments", n: 2 },
  { id: "copolpred", label: "copolpred", n: 3 },
  { id: "molytics", label: "molytics", n: 4 },
  { id: "ring", label: "ring", n: 5 },
  { id: "protectsus", label: "protectsus", n: 6 },
  { id: "memora", label: "memora", n: 7 },
  { id: "index", label: "index", n: 8 },
  { id: "papers", label: "bibliography", n: null },
  { id: "talks", label: "talks", n: null },
  { id: "honors", label: "honors", n: 9 },
  { id: "stack", label: "stack", n: 10 },
  { id: "contact", label: "contact", n: 11 },
];

export const HOME_FILE = {
  id: "home",
  name: "samarpan_mohanty.ipynb",
  kind: "home",
};

export const fileTree = [
  HOME_FILE,
  {
    id: "projects",
    name: "projects",
    kind: "dir",
    children: projects.map((p) => ({
      id: p.id,
      name: `${p.id}.ipynb`,
      kind: "project",
    })),
  },
  {
    id: "papers",
    name: "papers",
    kind: "dir",
    children: publications.map((p) => ({
      id: p.id,
      name: p.file,
      kind: "paper",
      href: p.href,
    })),
  },
  { id: "talks", name: "talks.md", kind: "talks" },
  {
    id: "cv",
    name: "Samarpan_Mohanty_CV.pdf",
    kind: "pdf",
    href: personalInfo.cv,
  },
];
